import React, { useEffect, useRef, useState } from 'react';
import DatePicker from '../global/DatePicker';
import { createUtcDateOnly, isValidDate } from '../../utils/dateOnly';

const key = value => isValidDate(value) ? value.getTime() : null;
const pad = value => String(value).padStart(2, '0');
export const formatReportDateInput = value => key(value) === null ? ''
  : `${pad(value.getUTCDate())}/${pad(value.getUTCMonth() + 1)}/${value.getUTCFullYear()}`;

export const parseReportDateInput = (text, boundary = 'end') => {
  const match = text.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [day, month, year] = match.slice(1).map(Number);
  return createUtcDateOnly(year, month - 1, day, boundary);
};

// Text entry is independent of the calendar so partial typing is never replaced
// by a selected Date. Invalid text clears the submitted value instead of silently
// retaining an older valid date.
export default function ReportDateInput({ id, name, value, onChange, onBlur, disabled = false, className = '', boundary = 'end' }) {
  const [text, setText] = useState(() => formatReportDateInput(value));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const lastEmitted = useRef(key(value));
  useEffect(() => {
    if (key(value) !== lastEmitted.current) {
      lastEmitted.current = key(value);
      setText(formatReportDateInput(value));
    }
  }, [value]);
  const emit = date => { lastEmitted.current = key(date); onChange(date); };
  const invalid = text.trim() !== '' && !parseReportDateInput(text, boundary);
  return <div>
    <div className="flex items-center gap-2">
      <input id={id} name={name} type="text" value={text} disabled={disabled}
        placeholder="DD/MM/YYYY" autoComplete="off" className={className}
        aria-invalid={!!invalid} aria-describedby={`${id}-help`}
        onBlur={onBlur} onChange={event => { setText(event.target.value); emit(parseReportDateInput(event.target.value, boundary)); }} />
      <button type="button" disabled={disabled} className="button-red" aria-expanded={calendarOpen}
        onClick={() => setCalendarOpen(open => !open)}>Choose date</button>
    </div>
    <p id={`${id}-help`} className="text-sm mt-1">Enter DD/MM/YYYY, or choose a date.</p>
    {invalid && <p role="alert" className="text-red-700">Enter a valid date, for example 14/12/2024.</p>}
    {calendarOpen && <div className="mt-2" role="group" aria-label="Reporting date calendar">
      <DatePicker inline boundary={boundary} selected={key(value) === null ? null : value}
        onChange={date => { setText(formatReportDateInput(date)); emit(date); }}
        showMonthDropdown showYearDropdown dropdownMode="select" disabled={disabled} />
      <button type="button" className="button-red block mt-2" onClick={() => setCalendarOpen(false)}>Done</button>
    </div>}
  </div>;
}

